import { useState, useEffect, useRef } from "react";
import { DraftVariations } from "../types/creator-studio";
import { BrandDetails } from "../types/add-brand-types";
import { PostContentPayload } from "../types/post-content";
import usePfmPostSocialApi from "../api/use-pfm-post-social.api";
import { deriveTwitterDraft, normalizeSocialDraftText } from "../utils/social-draft";

export interface MediaMetadata {
  url: string;
  width: number;
  height: number;
  size: number;
  aspectRatio: number;
  type: "image" | "video";
  duration?: number; // For videos (in seconds)
}

export const usePostSocial = (
  brandDetails: BrandDetails,
  selectedVariation?: DraftVariations,
  historyContent?: PostContentPayload,
  prefilledScheduleDate?: Date,
  prefilledPlatforms?: string[]
) => {
  const normalizedSelectedBody = normalizeSocialDraftText(
    selectedVariation?.body ?? ""
  );
  const normalizedHistoryBody = normalizeSocialDraftText(
    historyContent?.content ?? ""
  );
  const [postTitle, setPostTitle] = useState(
    historyContent?.title ?? selectedVariation?.subject ?? ""
  );
  const [postBody, setPostBody] = useState(
    normalizedHistoryBody || normalizedSelectedBody
  );

  // Twitter-specific body (independent from shared postBody)
  const [tweetBody, setTweetBody] = useState(
    normalizedHistoryBody || deriveTwitterDraft(normalizedSelectedBody)
  );

  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(
    historyContent?.platforms ??
      prefilledPlatforms ??
      brandDetails.social_profile_ids?.accounts?.map(
        (account) => account.platform
      ) ??
      []
  );
  const [scheduleOption, setScheduleOption] = useState<"now" | "later">(
    historyContent?.scheduled || prefilledScheduleDate ? "later" : "now"
  );
  const [scheduledDate, setScheduledDate] = useState<Date | null>(
    historyContent?.scheduledFor
      ? new Date(historyContent.scheduledFor)
      : prefilledScheduleDate ?? new Date()
  );
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [mediaPreviewUrls, setMediaPreviewUrls] = useState<string[]>([]);
  const [mediaMetadata, setMediaMetadata] = useState<MediaMetadata[]>([]);

  // Twitter thread state
  const [threadEnabled, setThreadEnabled] = useState(false);
  const [threadTweets, setThreadTweets] = useState<string[]>([""]);

  // Instagram story state
  const [isStory, setIsStory] = useState(false);

  // Reddit subreddits
  const [subreddits, setSubreddits] = useState<string[]>(
    historyContent?.subreddits ?? []
  );

  const { createPost, updatePost, uploadMedia, searchSubreddit } =
    usePfmPostSocialApi();

  // Keep track of all URLs for cleanup
  const urlsRef = useRef<Set<string>>(new Set());

  // Cleanup all blob URLs on unmount
  useEffect(() => {
    const urlsToCleanup = urlsRef.current;
    return () => {
      urlsToCleanup.forEach((url) => {
        URL.revokeObjectURL(url);
      });
      urlsToCleanup.clear();
    };
  }, []);

  const togglePlatform = (id: string) => {
    setSelectedPlatforms((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const handleMediaUpload = async (files: File[]) => {
    setMediaFiles((prev) => [...prev, ...files]);

    // Extract metadata for each file
    const metadataPromises = files?.map((file) => {
      return new Promise<MediaMetadata>((resolve) => {
        const url = URL.createObjectURL(file);
        const isVideo = file.type.startsWith("video/");

        if (isVideo) {
          // Handle video
          const video = document.createElement("video");
          video.preload = "metadata";

          video.onloadedmetadata = () => {
            resolve({
              url,
              width: video.videoWidth,
              height: video.videoHeight,
              size: file.size,
              aspectRatio: video.videoWidth / video.videoHeight,
              type: "video",
              duration: video.duration,
            });
            // Don't revoke the URL here - we need it for preview
          };

          video.onerror = () => {
            resolve({
              url,
              width: 0,
              height: 0,
              size: file.size,
              aspectRatio: 0,
              type: "video",
              duration: 0,
            });
          };

          video.src = url;
        } else {
          // Handle image
          const img = new Image();

          img.onload = () => {
            resolve({
              url,
              width: img.width,
              height: img.height,
              size: file.size,
              aspectRatio: img.width / img.height,
              type: "image",
            });
          };

          img.onerror = () => {
            resolve({
              url,
              width: 0,
              height: 0,
              size: file.size,
              aspectRatio: 0,
              type: "image",
            });
          };

          img.src = url;
        }
      });
    });

    const newMetadata = await Promise.all(metadataPromises);

    // Track URLs for cleanup
    newMetadata.forEach((m) => urlsRef.current.add(m.url));

    setMediaMetadata((prev) => [...prev, ...newMetadata]);
    setMediaPreviewUrls((prev) => [...prev, ...newMetadata.map((m) => m.url)]);
  };

  const removeMedia = (index: number) => {
    // Revoke the blob URL to free up memory
    const urlToRevoke = mediaPreviewUrls[index];
    if (urlToRevoke) {
      URL.revokeObjectURL(urlToRevoke);
      urlsRef.current.delete(urlToRevoke);
    }

    setMediaFiles((prev) => prev.filter((_, i) => i !== index));
    setMediaPreviewUrls((prev) => prev.filter((_, i) => i !== index));
    setMediaMetadata((prev) => prev.filter((_, i) => i !== index));
  };

  const reorderMedia = (fromIndex: number, toIndex: number) => {
    setMediaFiles((prev) => {
      const newFiles = [...prev];
      const [removed] = newFiles.splice(fromIndex, 1);
      newFiles.splice(toIndex, 0, removed);
      return newFiles;
    });
    setMediaPreviewUrls((prev) => {
      const newUrls = [...prev];
      const [removed] = newUrls.splice(fromIndex, 1);
      newUrls.splice(toIndex, 0, removed);
      return newUrls;
    });
    setMediaMetadata((prev) => {
      const newMetadata = [...prev];
      const [removed] = newMetadata.splice(fromIndex, 1);
      newMetadata.splice(toIndex, 0, removed);
      return newMetadata;
    });
  };

  return {
    postTitle,
    setPostTitle,
    postBody,
    setPostBody,
    tweetBody,
    setTweetBody,
    scheduleOption,
    setScheduleOption,
    scheduledDate,
    setScheduledDate,
    togglePlatform,
    selectedPlatforms,
    mediaFiles,
    mediaPreviewUrls,
    mediaMetadata,
    handleMediaUpload,
    removeMedia,
    reorderMedia,
    createPost,
    updatePost,
    uploadMedia,
    threadEnabled,
    setThreadEnabled,
    threadTweets,
    setThreadTweets,
    isStory,
    setIsStory,
    subreddits,
    setSubreddits,
    searchSubreddit,
  };
};
